import { useState } from 'react'
import { api, errorText } from '../api/client'
import type { AppointmentKind, Subject } from '../api/types'
import { subjectKey } from '../lib/subject'
import { useAsync } from '../lib/useAsync'
import { DateField } from './DateField'
import { Icon } from './Icon'
import { ErrorState, Loading } from './States'
import { useConfirm } from './Confirm'
import { useToast } from './Toast'
import { openExternal } from '../lib/google'

const KINDS: { value: AppointmentKind; label: string }[] = [
  { value: 'consultation', label: 'Consultation' },
  { value: 'application', label: 'Application' },
  { value: 'signing', label: 'Signing' },
  { value: 'closing', label: 'Closing' },
  { value: 'other', label: 'Other' },
]

const DURATIONS = [15, 30, 45, 60, 90] as const

/** A <input type="datetime-local"> value for the next round half-hour, local time.
 *  Starting from "soon" rather than an empty box is the difference between two
 *  clicks and typing a date. */
function nextHalfHour(): string {
  const d = new Date()
  d.setMinutes(d.getMinutes() > 30 ? 60 : 30, 0, 0)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

function whenLabel(iso: string, allDay: boolean): string {
  const d = new Date(iso)
  const date = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  if (allDay) return `${date} · all day`
  return `${date} · ${d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`
}

/** Meetings on one record: what is booked, and a line to book another.
 *
 *  Past appointments are kept and shown greyed rather than hidden — "when did I
 *  last sit down with them" is the question this panel is really for. */
export function AppointmentsPanel({ subject, refreshKey = 0, onChange }: {
  subject: Subject
  refreshKey?: number
  onChange?: () => void
}) {
  const { data, error, loading, reload } = useAsync(
    () => api.appointments(subject),
    [subjectKey(subject), refreshKey],
  )
  const [title, setTitle] = useState('')
  const [startsAt, setStartsAt] = useState(nextHalfHour)
  const [minutes, setMinutes] = useState<number>(30)
  const [kind, setKind] = useState<AppointmentKind>('consultation')
  const [busy, setBusy] = useState(false)
  const toast = useToast()
  const confirm = useConfirm()

  const refresh = () => { reload(); onChange?.() }

  const book = async () => {
    const clean = title.trim()
    if (!clean || !startsAt) return
    setBusy(true)
    try {
      const ends = new Date(new Date(startsAt).getTime() + minutes * 60_000)
      const saved = await api.createAppointment({
        ...subject, title: clean, kind,
        starts_at: startsAt,
        // Sent explicitly rather than leaning on the server's default hour, so
        // the duration the officer picked is the duration that lands on Google.
        ends_at: ends.toISOString(),
      })
      setTitle('')
      setStartsAt(nextHalfHour())
      toast(saved.google_event_id ? 'Booked, and on your Google Calendar.' : 'Appointment booked.')
      refresh()
    } catch (e) {
      toast(errorText(e), { tone: 'warning' })
    } finally {
      setBusy(false)
    }
  }

  const remove = async (id: number, name: string) => {
    if (!(await confirm({
      title: 'Delete this appointment?',
      body: `“${name}” will be removed here and from your Google Calendar.`,
      confirmLabel: 'Delete',
      danger: true,
    }))) return
    try {
      await api.deleteAppointment(id)
      refresh()
    } catch (e) {
      toast(errorText(e), { tone: 'warning' })
    }
  }

  if (loading && !data) return <Loading />
  if (error || !data) return <ErrorState error={error} onRetry={reload} />

  const now = Date.now()

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 14 }}>
        <input className="field" style={{ flex: '2 1 200px' }} value={title} placeholder="What is the meeting?"
          onChange={(e) => setTitle(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') book() }} />
        <DateField value={startsAt} onChange={setStartsAt} label="Starts at" withTime style={{ flex: '1 1 310px' }} />
        <select className="field" style={{ width: 92 }} value={minutes}
          onChange={(e) => setMinutes(Number(e.target.value))} aria-label="How long">
          {DURATIONS.map((m) => <option key={m} value={m}>{m} min</option>)}
        </select>
        <select className="field" style={{ width: 140 }} value={kind}
          onChange={(e) => setKind(e.target.value as AppointmentKind)} aria-label="Type of meeting">
          {KINDS.map((k) => <option key={k.value} value={k.value}>{k.label}</option>)}
        </select>
        <button className="btn primary" onClick={book} disabled={busy || !title.trim()}>
          {busy ? 'Booking…' : 'Book'}
        </button>
      </div>

      {data.calendar.connected ? null : (
        <p className="quiet" style={{ fontSize: 12, marginTop: -6, marginBottom: 12 }}>
          These stay in the CRM until you connect a calendar under Customize → Calendar.
        </p>
      )}

      {data.appointments.length === 0 ? (
        <p className="quiet" style={{ fontSize: 13, margin: 0 }}>Nothing booked with them yet.</p>
      ) : (
        <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
          {data.appointments.map((a) => {
            const past = new Date(a.ends_at).getTime() < now
            return (
              <li key={a.id} style={{
                display: 'flex', gap: 10, alignItems: 'baseline',
                padding: '8px 0', borderTop: '1px solid var(--rule)', opacity: past ? 0.55 : 1,
              }}>
                <span className="mono" style={{ fontSize: 12.5, minWidth: 124, flexShrink: 0 }}>
                  {whenLabel(a.starts_at, a.all_day)}
                </span>
                <span style={{ flex: 1, fontSize: 13 }}>
                  {a.title}
                  {a.location ? <span className="quiet"> · {a.location}</span> : null}
                  {a.origin === 'google' ? (
                    <span className="quiet" title="Created on your Google Calendar">
                      {' '}<Icon name="event_available" size={13} style={{ verticalAlign: -2 }} />
                    </span>
                  ) : null}
                </span>
                {a.google_html_link ? (
                  <button className="icon-btn-plain" title="Open in Google Calendar — to add guests or a Meet link"
                    aria-label={`Open ${a.title} in Google Calendar`}
                    onClick={() => openExternal(a.google_html_link!)}>
                    <Icon name="open_in_new" size={15} />
                  </button>
                ) : null}
                <button className="icon-btn-plain" aria-label={`Delete ${a.title}`}
                  onClick={() => remove(a.id, a.title)}>
                  <Icon name="delete" size={16} />
                </button>
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}
